import React, { useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { Table, Card, Typography, Row, Grid, Select, Space, Button, Statistic, Col, Input } from 'antd';
import { JhiItemCount, getSortState, JhiPagination, TextFormat } from 'react-jhipster';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { getEcoleComptesTransactions } from 'app/entities/transaction/transaction.reducer';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { ITEMS_PER_PAGE } from 'app/shared/util/pagination.constants';
import dayjs, { Dayjs } from 'dayjs';
import type { RangeValue } from 'rc-picker/lib/interface';
import generatePicker from 'antd/es/date-picker/generatePicker';
import dayjsGenerateConfig from 'rc-picker/lib/generate/dayjs';
import { EyeOutlined } from '@ant-design/icons';
import { APP_TIMESTAMP_FORMAT } from 'app/config/constants';
import { getPaymentMethod } from 'app/utils/paymentUtils';
import { getEcoleWithDetails } from '../ecole/ecole.reducer';
import { render } from '@fullcalendar/core/preact';

const { RangePicker } = generatePicker<Dayjs>(dayjsGenerateConfig);

const { Title } = Typography;
const { useBreakpoint } = Grid;
const { Option } = Select;

const TransactionListPage = (props: RouteComponentProps<{ ecoleid: string; anneeScolaireId: string }>) => {
  const dispatch = useAppDispatch();
  const screens = useBreakpoint();
  const transactionList = useAppSelector(state => state.transaction.entities);
  const totalItems = useAppSelector(state => state.transaction.totalItems);
  const loading = useAppSelector(state => state.transaction.loading);
  const ecole = useAppSelector(state => state.ecole.entityDetails);

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getSortState(props.location, ITEMS_PER_PAGE, 'id'), props.location.search)
  );
  const ecoleId = props.match.params.ecoleid;
  const anneeScolaireId = props.match.params.anneeScolaireId;

  const [filters, setFilters] = useState<{
    type?: string;
    startDate?: string;
    endDate?: string;
    description?: string;
    moyenPaiement?: string;
  }>({});

  const fetchData = () => {
    dispatch(
      getEcoleComptesTransactions({
        ecoleId: props.match.params.ecoleid,
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        ...filters,
      })
    );
  };

  useEffect(() => {
    dispatch(getEcoleWithDetails(ecoleId));
    fetchData();
  }, [paginationState.activePage, paginationState.itemsPerPage, filters]);

  const handlePagination = (currentPage: number) => {
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });
  };

  const handleTypeChange = (value: string | undefined) => {
    setFilters(prev => ({ ...prev, type: value }));
    setPaginationState({ ...paginationState, activePage: 1 });
  };

  const handleMPayChange = (value: string | undefined) => {
    setFilters(prev => ({ ...prev, moyenPaiement: value }));
    setPaginationState({ ...paginationState, activePage: 1 });
  };

  const handleDateRangeChange = (dates: RangeValue<Dayjs>) => {
    if (dates && dates[0] && dates[1]) {
      setFilters(prev => ({
        ...prev,
        startDate: dates[0].startOf('day').format('YYYY-MM-DD'),
        endDate: dates[1].endOf('day').format('YYYY-MM-DD'),
      }));
    } else {
      setFilters(prev => ({ ...prev, startDate: undefined, endDate: undefined }));
    }
    setPaginationState({ ...paginationState, activePage: 1 });
  };

  const soldeTotal = (ecole?.compte?.solde || 0) + (ecole?.compteSalaire?.solde || 0);

  const navigate = (pathname: string) => props.history.push(pathname);
  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (text: string) => <TextFormat type="date" value={text} format={APP_TIMESTAMP_FORMAT} />,
    },
    {
      title: 'Montant',
      dataIndex: 'montant',
      key: 'montant',
      align: 'right' as const,
      render(value: number, record: any) {
        const sign = record.type === 'FACTURE' || record.type === 'DEPOT' ? '' : '-';
        return <span>{`${sign}${value.toLocaleString('fr-FR')} F`}</span>;
      },
    },

    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: 'Partenaire',
      dataIndex: 'moyenPaiement',
      key: 'moyenPaiement',
      render(moyenPaiement: string) {
        const color = moyenPaiement === 'WAVE' ? '#1890ff' : moyenPaiement === 'OM' ? '#f28e19' : 'inherit';
        return <span style={{ color, fontWeight: 'bold' }}>{moyenPaiement === 'OM' ? 'Orange Money' : moyenPaiement}</span>;
      },
    },
    {
      title: 'Statut',
      dataIndex: 'transactionStatus',
      key: 'transactionStatus',
      render(transactionStatus: string) {
        const color = transactionStatus === 'SUCCESS' ? '#52c41a' : '#f5222d';
        const label = transactionStatus === 'SUCCESS' ? 'Succès' : 'Échoué';
        return <span style={{ color, fontWeight: 'bold' }}>{label}</span>;
      },
    },

    {
      title: 'Actions',
      dataIndex: 'reference',
      key: 'reference',
      width: screens.xs ? 100 : 200,
      render: (_: any, record) => (
        <Space size="middle">
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => {
              let path = '';
              switch (record.type) {
                case 'FACTURE':
                  path = `/scolarite/${ecoleId}/${anneeScolaireId}/${record.reference}/details`;
                  break;
                case 'SALAIRE':
                  path = `/salaires/${ecoleId}/${anneeScolaireId}/${record.reference}/details`;
                  break;
                case 'DEPOT':
                  path = `/approvisonnements/${ecoleId}/${anneeScolaireId}/${record.reference}/details`;
                  break;
                case 'RETRAIT':
                  path = `/virements/demande/${record.reference}/details`;
                  break;
                default:
                  path = `${props.match.url}/${record.reference}`;
              }
              navigate(path);
            }}
            size={screens.xs ? 'small' : 'middle'}
          />
        </Space>
      ),
    },
  ];

  return (
    <Card style={{ margin: screens.xs ? '16px 8px' : '24px' }}>
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={8}>
          <Card
            style={{
              backgroundColor: '#FFF3E5',
              border: '1px solid #FAD6B0',
              borderRadius: '12px',
              textAlign: 'center',
              padding: '16px',
              boxShadow: '0 1px 4px rgba(242, 142, 25, 0.1)',
            }}
          >
            <Statistic
              title={<span style={{ fontSize: '14px', color: '#F28E19' }}>Solde Principal</span>}
              value={soldeTotal.toLocaleString('fr-FR')}
              precision={0}
              valueStyle={{ color: '#F28E19', fontSize: '24px', fontWeight: 'bold' }}
              suffix="FCFA"
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <Card
            style={{
              backgroundColor: '#F6FFF4',
              border: '1px solid #B7EACD',
              borderRadius: '12px',
              textAlign: 'center',
              padding: '16px',
              boxShadow: '0 1px 4px rgba(82, 196, 26, 0.1)',
            }}
          >
            <Statistic
              title={<span style={{ fontSize: '14px', color: '#52c41a' }}>Solde Scolarité</span>}
              value={(ecole?.compte?.solde || 0).toLocaleString('fr-FR')}
              precision={0}
              valueStyle={{ color: '#52c41a', fontSize: '24px', fontWeight: 'bold' }}
              suffix="FCFA"
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <Card
            style={{
              backgroundColor: '#FFF0F0',
              border: '1px solid #FFBFBF',
              borderRadius: '12px',
              textAlign: 'center',
              padding: '16px',
              boxShadow: '0 1px 4px rgba(255, 77, 79, 0.1)',
            }}
          >
            <Statistic
              title={<span style={{ fontSize: '14px', color: '#ff4d4f' }}>Solde Salaire</span>}
              value={(ecole?.compteSalaire?.solde || 0).toLocaleString('fr-FR')}
              precision={0}
              valueStyle={{ color: '#ff4d4f', fontSize: '24px', fontWeight: 'bold' }}
              suffix="F CFA"
            />
          </Card>
        </Col>
      </Row>
      <div
        style={{
          padding: screens.xs ? '16px 0' : '24px 0',
          display: 'flex',
          flexDirection: screens.xs ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: screens.xs ? 'flex-start' : 'center',
          gap: screens.xs ? '16px' : '0',
        }}
      >
        <Title level={3}>Transactions</Title>

        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <Input
            placeholder="Rechercher par eleve ou professeur"
            allowClear
            value={filters.description}
            onChange={e => {
              setFilters(prev => ({
                ...prev,
                description: e.target.value,
              }));
              setPaginationState(prev => ({
                ...prev,
                activePage: 1,
              }));
            }}
            style={{ width: 260 }}
          />
          <Select
            placeholder="Filtrer par type de transaction"
            onChange={handleTypeChange}
            allowClear
            style={{ width: 'auto' }}
            value={filters.type}
          >
            <Option value="FACTURE">FACTURE</Option>
            <Option value="SALAIRE">SALAIRE</Option>
            <Option value="DEPOT">DEPOT</Option>
            <Option value="RETRAIT">RETRAIT</Option>
          </Select>

          <Select
            placeholder="Filtrer par moyen de paiement"
            onChange={handleMPayChange}
            allowClear
            style={{ width: 'auto' }}
            value={filters.moyenPaiement}
          >
            <Option value="WAVE">WAVE</Option>
            <Option value="OM">Orange Money</Option>
          </Select>

          <RangePicker
            format="DD/MM/YYYY"
            onChange={handleDateRangeChange}
            value={filters.startDate && filters.endDate ? [dayjs(filters.startDate), dayjs(filters.endDate)] : null}
          />
        </div>
      </div>

      <Table
        dataSource={transactionList}
        columns={columns}
        rowKey="id"
        pagination={false}
        loading={loading}
        bordered
        size="middle"
        style={{ backgroundColor: 'white' }}
      />

      {transactionList && transactionList.length > 0 && (
        <div>
          <Row className="justify-content-center">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </Row>
          <Row className="justify-content-center" style={{ marginTop: '16px' }}>
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </Row>
        </div>
      )}
    </Card>
  );
};

export default TransactionListPage;
